import { m } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  AgentPlaygroundContent,
  PlaygroundState,
} from "src/agents/AgentVersionPage/AgentPlayground";
import { usePlaygroundContext } from "src/agents/AgentVersionPage/PlaygroundContext";
import { SideDrawer, useSideDrawer } from "src/agents/base/SideDrawer";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";

type AgentPlaygroundDrawerProps = {
  agentId: string;
  versionId: string;
  agentName: string;
};

export const AgentPlaygroundDrawer: React.FC<AgentPlaygroundDrawerProps> = ({
  agentId,
  versionId,
  agentName,
}) => {
  const [playgroundState, setPlaygroundState] = useState<PlaygroundState>({
    type: "input",
  });

  const { playgroundId, currentSessionId, sessionStatus, ping, resetSession } =
    usePlaygroundContext();

  // Handle close with explicit cleanup - reset for next open
  const handleClose = useCallback(
    (accept: () => void) => {
      setPlaygroundState({ type: "input" });
      resetSession();
      accept();
    },
    [resetSession],
  );

  // Ping when drawer opens (detect via isOpen from useSideDrawer)
  const { isOpen } = useSideDrawer("playground", {
    onCloseRequested: handleClose,
  });
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      ping(); // Uses current (potentially fresh) session ID
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, ping]);

  const handleReset = useCallback(() => {
    setPlaygroundState({ type: "input" });
    resetSession();
  }, [resetSession]);

  // Show extra actions when there's an active session (booting is just pre-warming, not a session)
  const hasActiveSession =
    currentSessionId !== null &&
    sessionStatus !== null &&
    sessionStatus !== "booting";
  const extraActions = hasActiveSession ? (
    <m.div
      animate={{ opacity: 1 }}
      className="flex items-center gap-2"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: "easeIn" }}
    >
      {currentSessionId && (
        <CopyTextIcon
          dataLoc="copy-session-id-button"
          feedback="inline"
          size="xs"
          tooltip="Copy session ID"
          value={currentSessionId}
        />
      )}
      <Button
        dataLoc="test-reset-button"
        size="sm"
        variant="secondary"
        onClick={handleReset}
      >
        Reset
      </Button>
    </m.div>
  ) : undefined;

  return (
    <SideDrawer
      dataLoc="agent-playground-drawer"
      drawerId="playground"
      header={{
        title: "Test Agent",
        description: "Try out your agent to see how it responds.",
        extraActions,
        closeDataLoc: "close-test-panel",
      }}
    >
      <AgentPlaygroundContent
        agentId={agentId}
        agentName={agentName}
        playgroundId={playgroundId}
        playgroundState={playgroundState}
        versionId={versionId}
        onPlaygroundStateChange={setPlaygroundState}
      />
    </SideDrawer>
  );
};
